import { supabase } from './supabase'
import { fetchAll } from './fetchall'
import { formatDateLong } from './format'
import {
  dripTrigger,
  stepOccurrence,
  campaignIdOf,
  addDays,
  daysBetween,
  scheduleFor,
  dueTouches,
  dueReviewedTouches,
  type DripLane,
  type DripStatus,
  type DripEnrolmentMode,
  type DripCampaign,
  type DripTouch,
  type DripEnrolment,
  type DueTouch,
} from './drip-schedule'

/**
 * Re-exported, not redefined.
 *
 * `drips.ts` opens with a real Supabase client import for `loadDrips()`
 * below, which is exactly what a Cloudflare Function cannot carry — see
 * `drip-schedule.ts`'s own header for what that costs when gotten wrong. The
 * pure scheduling engine lives there instead, on its own, and this file
 * re-exports it so everything already importing `dripTrigger`, `dueTouches`
 * and the rest from `./drips` — `Drips.tsx`, `navcounts.ts`, the tests —
 * keeps working unchanged. Only `sync-metabase.ts` imports `drip-schedule.ts`
 * directly.
 */
export {
  dripTrigger,
  stepOccurrence,
  campaignIdOf,
  addDays,
  daysBetween,
  scheduleFor,
  dueTouches,
  dueReviewedTouches,
}
export type { DripLane, DripStatus, DripEnrolmentMode, DripCampaign, DripTouch, DripEnrolment, DueTouch }

/**
 * Drip campaigns — a named sequence of touches, each a fixed number of days
 * after the one before it.
 *
 * The scheduling engine itself (types, `dueTouches`, `scheduleFor` and the
 * rest) lives in `drip-schedule.ts` — see its header for why. This file adds
 * what only the browser tab needs: the database reads (`loadDrips`), and the
 * small UI-facing helpers (`enrolDescription`, `isCalendarDate`,
 * `resumeStart`, `progressFor`, `isComplete`) that have no reason to be
 * Function-safe because nothing server-side calls them.
 *
 * NOTHING IN THIS FILE SENDS ANYTHING. `dueTouches` answers "what is due", and
 * the answer is turned into `outreach_drafts` rows that wait for Renee. The
 * only thing a bug in here can do is put a card in front of her that she did
 * not expect — the same line the rest of the application is built on.
 *
 * WHY THERE IS NO SENDS TABLE
 *
 * A touch produces an ordinary draft tagged `trigger = 'drip:<id>'` and
 * `occurrence = 's<step>'`. `outreach_drafts` is already unique on
 * `(agent_id, trigger, occurrence)`, so touch 2 physically cannot reach the
 * same agent twice however many times this runs. Which means `dueTouches` does
 * not have to be careful about being called twice, and the browser can safely
 * work out what is due every time the tab is opened.
 */

/**
 * The only trigger there is, and the only one the daily Metabase run looks for.
 *
 * Named rather than typed out at each use because it appears in three places
 * that must agree exactly — the check constraint on `drip_campaigns`, the
 * `trigger_key=eq.new_agent` filter in the Pages Function, and the row this app
 * writes. A typo in the third is not a save error; it is a campaign that looks
 * armed and is never read.
 */
export const NEW_AGENT_TRIGGER = 'new_agent'

/**
 * Is this campaign one the daily run will actually put new agents into?
 *
 * FOUR things are checked, because the Pages Function makes four decisions —
 * three in the query, `enrolment=eq.trigger&trigger_key=eq.new_agent&
 * status=eq.active`, and then one more on the row it gets back:
 *
 *     if (!c.listens_from) continue   // switched on before the column existed
 *
 * A campaign satisfying three of the four does nothing at all, and this helper
 * is the only thing the screen has for deciding whether to tell her that real
 * people will be emailed automatically. It has to agree with the server
 * exactly, including the part that is not in the query.
 */
export const isListeningForNewAgents = (
  c: Pick<DripCampaign, 'enrolment' | 'trigger_key' | 'status' | 'listens_from'>,
): boolean =>
  c.enrolment === 'trigger' &&
  c.trigger_key === NEW_AGENT_TRIGGER &&
  c.status === 'active' &&
  !!c.listens_from

/**
 * How people get into a campaign, in her words rather than the schema's.
 *
 * 'trigger' is never shown as the word "trigger". The only trigger that exists
 * is new agents, so the label says new agents; if a second one is ever added
 * this becomes a lie and has to be reworded, which is the correct amount of
 * friction for adding a second one.
 */
export const ENROLMENT_LABEL: Record<DripEnrolmentMode, string> = {
  manual: 'You pick the people',
  trigger: 'New agents — added automatically',
}

/**
 * What the card says about how this campaign fills itself.
 *
 * The date is spelled out rather than printed as `2026-08-18`, because the
 * whole point of showing it is that somebody reads it and notices it is wrong.
 *
 * **Status is part of the answer, not a separate pill.** A paused or finished
 * campaign is not picking anybody up — the Pages Function filters on
 * `status=eq.active` — so saying "New agents, from 18 August" beside a `paused`
 * badge is two contradictory statements on one line, and the reassuring one is
 * the one that gets read.
 *
 * The no-date case should be unreachable, since
 * `drip_listens_from_matches_enrolment` refuses it, but it is stated plainly
 * rather than hidden: a trigger campaign with a null date enrols nobody and
 * would otherwise look identical to one that is working.
 */
export function enrolDescription(
  c: Pick<DripCampaign, 'enrolment' | 'trigger_key' | 'listens_from' | 'status'>,
): string {
  if (c.enrolment !== 'trigger') return ENROLMENT_LABEL.manual
  if (c.trigger_key !== NEW_AGENT_TRIGGER) {
    return `Set to a trigger this app does not know about (${c.trigger_key ?? 'none'}) — nobody will be added`
  }
  if (!c.listens_from) return 'New agents — but no start date is set, so nobody will be added'
  if (c.status !== 'active') {
    return `New agents from ${formatDateLong(c.listens_from)} — but nobody while this is ${c.status}`
  }
  return `New agents, from ${formatDateLong(c.listens_from)}`
}

/**
 * Is this a real day on the calendar, written `YYYY-MM-DD`?
 *
 * `<input type="date">` is not the guarantee it looks like. The year accepts
 * **four or more** digits, so `20026-08-18` is a value the picker will hand
 * over quite happily — and it defeats every check downstream, because all of
 * them are string comparisons: `'20026-08-18' > '2026-08-18'` is false, and so
 * is `'2026-09-01' < '20026-08-18'`. The campaign then gates nobody at all and
 * the only tell on screen is a date reading "August 18, 20026".
 *
 * The round trip through the platform's own calendar also refuses 31 February,
 * which is the same trap `parseUsDate` fell into on the server side.
 */
export function isCalendarDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return false
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  const dt = new Date(y, mo - 1, d)
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d
}

/**
 * The next step number for a campaign — never one that has been used before.
 *
 * `max(existing) + 1` is not enough, and the migration says why: a step number
 * is half of the `occurrence` on every draft that touch has written, so reusing
 * one re-points new content at old history. Delete touch 3 of a campaign 60
 * people have completed, add a replacement, and it is step 3 again: those 60
 * are marked as having had it and are skipped forever, while later enrolees get
 * the new wording under the same number. Two populations, different emails, no
 * way to tell them apart on screen.
 *
 * So the high-water mark includes steps that only exist in history now.
 */
export function nextStep(
  touches: readonly DripTouch[],
  /** Steps this campaign has ever written a draft for. */
  everSent: ReadonlySet<number>,
): number {
  const highest = Math.max(0, ...touches.map((t) => t.step), ...everSent)
  return highest + 1
}

/**
 * The `started_on` to give somebody being put back into a campaign, so their
 * remaining touches run from today rather than from a schedule that expired
 * while they were out.
 *
 * WHY THIS EXISTS AND IS NOT JUST "SET status = active".
 *
 * `dueTouches` offers one touch per pass, which reads as a sensible drip until
 * you remember that approving reloads the screen. Somebody enrolled in January,
 * sent touches 1 and 2, stopped in February and put back in August has touches
 * 3, 4 and 5 all long overdue: approve 3, the page reloads, 4 is due, approve
 * it, 5 is due. Three emails in three clicks on one morning, each one presented
 * as an ordinary due touch. The gate is per-render, not per-day.
 *
 * Moving their start date forward so the NEXT unsent touch falls today makes
 * the rest of the sequence run at its proper spacing from the moment they come
 * back — which is what "put them back in" means to anybody who is not looking
 * at the database.
 *
 * Touches already sent stay sent: this only moves the anchor, and `alreadySent`
 * is what decides delivery. Somebody with nothing left to send keeps their
 * original date, because there is no next touch to line up.
 */
export function resumeStart(
  startedOn: string,
  touches: readonly DripTouch[],
  sentSteps: ReadonlySet<number>,
  today: string,
): string {
  const ordered = [...touches].sort((a, b) => a.step - b.step)
  let offset = 0
  for (let i = 0; i < ordered.length; i++) {
    offset += i === 0 ? 0 : ordered[i].delay_days
    if (!sentSteps.has(ordered[i].step)) {
      // Never drag somebody's dates *backwards*. If their next touch is still
      // in the future — stopped and restarted the same week — leave the
      // schedule exactly as it was.
      const wouldBe = addDays(today, -offset)
      return wouldBe > startedOn ? wouldBe : startedOn
    }
  }
  return startedOn
}

/**
 * Where one agent has got to. What the per-agent progress line reads from.
 *
 * `sentSteps` is what has actually gone out, not what the calendar says should
 * have — a campaign she has not approved for a fortnight should say "touch 2 of
 * 4, due five days ago", not claim touch 2 has happened.
 */
export function progressFor(
  enrolment: DripEnrolment,
  touches: readonly DripTouch[],
  sentSteps: ReadonlySet<number>,
): { done: number; total: number; nextStep: number | null; nextDue: string | null } {
  const ordered = [...touches].sort((a, b) => a.step - b.step)
  const schedule = scheduleFor(enrolment.started_on, ordered)
  const done = ordered.filter((t) => sentSteps.has(t.step)).length
  if (enrolment.status !== 'active') {
    return { done, total: ordered.length, nextStep: null, nextDue: null }
  }
  const next = schedule.find((s) => !sentSteps.has(s.step))
  return {
    done,
    total: ordered.length,
    nextStep: next?.step ?? null,
    nextDue: next?.due ?? null,
  }
}

/**
 * Has every active enrolment had every touch?
 *
 * Answered from the enrolments rather than from a date, because a campaign with
 * somebody still mid-sequence is not finished however old it is. A campaign
 * with nobody in it is NOT finished either — a trigger campaign waiting for its
 * first new agent would otherwise report itself complete on the day it was
 * made.
 */
export function isComplete(
  touches: readonly DripTouch[],
  enrolments: readonly DripEnrolment[],
  sentByAgent: ReadonlyMap<string, ReadonlySet<number>>,
): boolean {
  if (touches.length === 0) return false
  const active = enrolments.filter((e) => e.status === 'active')
  if (active.length === 0) return false
  return active.every((e) => {
    const sent = sentByAgent.get(e.agent_id)
    return !!sent && touches.every((t) => sent.has(t.step))
  })
}

// --------------------------------------------------------------- the database

const readable = (message: string): string => {
  const m = message.toLowerCase()
  if (m.includes('permission denied') || m.includes('jwt') || m.includes('not authorized')) {
    return 'Your sign-in expired. Refresh the page and sign in again — nothing was changed.'
  }
  if (m.includes('drip_campaigns') || m.includes('drip_touches') || m.includes('drip_enrolments')) {
    return `${message} — the drip tables may not have been created yet.`
  }
  return message
}

export type DripBundle = {
  campaigns: DripCampaign[]
  touches: DripTouch[]
  enrolments: DripEnrolment[]
  /** `${campaignId} ${agentId} ${step}` for every touch already written. */
  sent: Set<string>
  /**
   * How many of a campaign's emails are still sitting unsent in the Outbox.
   *
   * Only used to tell her what deleting a campaign will and will not take with
   * it. Those emails are real queued work and are deliberately left alone; a
   * count is the difference between that being a considered decision and
   * looking like something the delete forgot.
   */
  pending: Map<string, number>
  error: string | null
  /** Set when a read was cut short — the screen must not read as complete. */
  warning: string | null
}

/**
 * Everything the tab needs, in four reads.
 *
 * Paged, all of them. A plain `.select()` stops at `db.max_rows` and says
 * nothing about it — and enrolments are the table that gets there first: one
 * quarterly campaign over the whole roster is 785 rows on its own, and the
 * silent truncation would show as a campaign that had simply forgotten some of
 * its people.
 */
/**
 * Every draft this feature has ever written, and nothing else.
 *
 * `fetchAll` takes an equality filter and what is wanted here is a prefix, so
 * this pages by hand rather than widening the one function the whole app's
 * paging correctness rests on.
 *
 * The first version read the WHOLE of `outreach_drafts` and filtered in memory.
 * That is fine at eleven rows and indefensible at a realistic year's volume —
 * 785-recipient manual sends, plus milestones, plus four drip touches across
 * the roster is comfortably five figures, which is twenty-plus sequential round
 * trips. And it ran after every single action on the tab.
 */
async function dripDrafts(): Promise<{
  rows: { agent_id: string; trigger: string; occurrence: string; status: string }[]
  error: string | null
}> {
  const rows: { agent_id: string; trigger: string; occurrence: string; status: string }[] = []
  const PAGE = 500
  for (let page = 0; page < 200; page++) {
    const from = page * PAGE
    const { data, error } = await supabase
      .from('outreach_drafts')
      .select('agent_id,trigger,occurrence,status')
      .like('trigger', 'drip:%')
      // Total order, ending in a unique column. `created_at` ties across a
      // whole batch insert, so paging on it alone drops or repeats rows.
      .order('agent_id', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) return { rows, error: error.message }
    const got = (data ?? []) as typeof rows
    rows.push(...got)
    if (got.length < PAGE) return { rows, error: null }
  }
  return { rows, error: null }
}

export async function loadDrips(): Promise<DripBundle> {
  const empty: DripBundle = {
    campaigns: [], touches: [], enrolments: [], sent: new Set(), pending: new Map(),
    error: null, warning: null,
  }

  const campaigns = await fetchAll<DripCampaign>('drip_campaigns', '*', ['created_at', 'id'])
  if (campaigns.error) return { ...empty, error: readable(campaigns.error) }

  const touches = await fetchAll<DripTouch>('drip_touches', '*', ['campaign_id', 'step'])
  if (touches.error) return { ...empty, error: readable(touches.error) }

  const enrolments = await fetchAll<DripEnrolment>('drip_enrolments', '*', ['campaign_id', 'id'])
  if (enrolments.error) return { ...empty, error: readable(enrolments.error) }

  const drafts = await dripDrafts()
  if (drafts.error) return { ...empty, error: readable(drafts.error) }

  const sent = new Set<string>()
  const pending = new Map<string, number>()
  for (const d of drafts.rows) {
    const id = campaignIdOf(d.trigger)
    if (!id) continue
    if (d.status === 'pending') pending.set(id, (pending.get(id) ?? 0) + 1)
    /**
     * Only `s<number>` counts. A row filed under this campaign with an
     * occurrence in any other shape is not a touch — and `Number('sx')` is NaN,
     * which would land in the set as `"c a NaN"` and match nothing, so a bad
     * row would silently look like "never sent" and the touch would be offered
     * again forever.
     */
    const m = /^s(\d+)$/.exec(d.occurrence)
    if (m) sent.add(`${id} ${d.agent_id} ${Number(m[1])}`)
  }

  const truncated = [campaigns, touches, enrolments].some((r) => r.truncated)

  return {
    campaigns: campaigns.rows,
    touches: touches.rows,
    enrolments: enrolments.rows,
    sent,
    pending,
    error: null,
    warning: truncated
      ? 'This list is incomplete — the read stopped early, so a campaign may be missing people or history. Reload before approving anything.'
      : null,
  }
}
